import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { flexStyle, gridStyle, rgba, textStyle } from '@themes/mixins';
import styled, { css } from 'styled-components';

import Icon from '../Icon';

import { Sizes } from './enums';
import { Props } from './types';

export const Root = styled.div<Partial<Props>>`
  ${flexStyle({
    align: 'flex-start',
    direction: 'column',
    justify: 'flex-start',
    wrap: 'nowrap',
  })};
`;
export const ButtonsListContainer = styled.div`
  ${gridStyle({ columnGap: 8, rowGap: 12, columns: 5, rows: 2 })};
  padding: 6px 6px;
`;
export const DateButton = styled.span`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
  background-color: ${({ theme }) => theme.colors.divider};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 10px;
  padding: 4px 6px;
  cursor: pointer;

  &:hover {
    border: 1px solid ${({ theme }) => theme.colors.secondaryBackground};
  }
`;
export const Label = styled.label<{ error?: boolean }>`
  ${flexStyle({ align: 'center', wrap: 'nowrap' })};
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
  margin-bottom: 8px;
  letter-spacing: 0.5px;

  ${({ error, theme }) =>
    error &&
    css`
      color: ${theme.colors.red};
    `}
`;
export const RequiredMark = styled.span<{ error?: boolean }>`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
  margin-left: 5px;

  ${({ error, theme }) =>
    error &&
    css`
      color: ${theme.colors.red};
    `}
`;
export const StyledDesktopDatePicker = styled(DesktopDatePicker)<{
  fullWidth?: boolean;
  sizeType: Sizes;
}>`
  color: ${({ theme }) => theme.colors.textPrimary};

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
  & label {
    color: ${({ theme }) => theme.colors.textSecondary};
    ${({ sizeType }) => {
      switch (sizeType) {
        case Sizes.small:
          return css`
            font-size: 14px;
            top: -10px;
          `;
        default:
          return css``;
      }
    }}
  }

  & label.Mui-focused {
    color: ${({ theme }) => theme.colors.textPrimary};
    top: 0;
  }

  & label.MuiFormLabel-filled {
    top: 0;
  }

  & .MuiInputLabel-formControl.Mui-error {
    color: ${({ theme }) => theme.colors.red};

    & .MuiInputLabel-asterisk.Mui-error {
      color: ${({ theme }) => theme.colors.red};
    }
  }

  & .MuiIconButton-root {
    & .MuiSvgIcon-root {
      ${({ sizeType }) => {
        switch (sizeType) {
          case Sizes.small:
            return css`
              font-size: 20px;
            `;
          default:
            return css``;
        }
      }}
    }
  }

  & .MuiOutlinedInput-root {
    color: ${({ theme }) => theme.colors.textPrimary};
    padding-right: 10px;

    & fieldset {
      border-color: ${({ theme }) => theme.colors.divider};
    }

    &:hover fieldset {
      border-color: ${({ theme }) => theme.colors.textSecondary};
    }

    &.Mui-focused fieldset {
      border-color: ${({ theme }) => theme.colors.textSecondary};
    }

    & .MuiIconButton-edgeEnd {
      color: ${({ theme }) => theme.colors.textSecondary};
      padding: 0 5px 0 0;
    }

    & .clearButton {
      color: ${({ theme }) => theme.colors.textSecondary};
      padding: 0 4px 0 0;
    }

    & input {
      ${({ sizeType }) => {
        switch (sizeType) {
          case Sizes.small:
            return css`
              font-size: 12px;
              padding: 6px 4px 6px 12px;
            `;
          case Sizes.medium:
            return css`
              padding: 12px 0 12px 14px;
            `;
          default:
            return css``;
        }
      }}
    }

    &.Mui-readOnly {
      background-color: ${({ theme }) => rgba(theme.colors.divider, 10)};
    }

    &.Mui-error {
      color: ${({ theme }) => theme.colors.red};

      & .MuiInputAdornment-root .MuiIconButton-root {
        color: ${({ theme }) => theme.colors.red};
      }

      & .MuiOutlinedInput-input::placeholder {
        color: ${({ theme }) => theme.colors.red};
      }

      &.Mui-focused fieldset {
        border-color: ${({ theme }) => theme.colors.red};
      }

      & fieldset {
        color: ${({ theme }) => theme.colors.red};
      }

      &:hover fieldset {
        border-color: ${({ theme }) => theme.colors.red};
      }
    }
  }
`;
export const ErrorMessageContainer = styled.div<{
  sizetype: Sizes;
}>`
  ${flexStyle({ align: 'center', wrap: 'nowrap' })};
  ${({ sizetype }) => {
    switch (sizetype) {
      case Sizes.small:
        return css`
          margin-top: 2px;
        `;
      case Sizes.medium:
        return css`
          margin-top: 8px;
        `;
      default:
        return css`
          margin-top: 8px;
        `;
    }
  }}
`;
export const AttentionIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.red};
  margin-right: 4px;
`;
export const ErrorMessage = styled.span`
  ${textStyle({
    color: 'red',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
  letter-spacing: 0.4px;
`;
